import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, Clock, AlertTriangle, CheckCircle2, XCircle, ChevronLeft,
  Loader2, RefreshCw, Mail, Phone, FileText,
} from "lucide-react";
import { api } from "../lib/api";
import { usePageTitle } from "../hooks/usePageTitle";

const STATUSES = [
  { id: "received", label: "Recebida", color: "bg-blue-100 text-blue-800 border-blue-200" },
  { id: "in_progress", label: "Em análise", color: "bg-amber-100 text-amber-900 border-amber-200" },
  { id: "responded", label: "Respondida", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { id: "rejected", label: "Rejeitada", color: "bg-slate-200 text-slate-700 border-slate-300" },
];

const statusMeta = (id) => STATUSES.find((s) => s.id === id) || STATUSES[0];

const DaysBadge = ({ days, status }) => {
  const done = status === "responded" || status === "rejected";
  if (done) {
    return <span className="text-[10px] text-slate-500">Concluída</span>;
  }
  if (days < 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-200">
        <AlertTriangle className="w-3 h-3" /> {Math.abs(days)}d vencido
      </span>
    );
  }
  const cls = days <= 3 ? "bg-amber-100 text-amber-900 border-amber-200"
    : "bg-emerald-50 text-emerald-800 border-emerald-200";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${cls}`}>
      <Clock className="w-3 h-3" /> {days}d restantes
    </span>
  );
};

export default function AdminLgpd() {
  usePageTitle(
    "Governança LGPD",
    "Governança das solicitações LGPD Art. 18 — recebidas, em análise, respondidas, rejeitadas e com prazo estourado."
  );

  const [data, setData] = useState({ items: [], counters: {} });
  const [statusFilter, setStatusFilter] = useState("");
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // opened request
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (statusFilter) params.set("status", statusFilter);
      const { data: d } = await api.get(`/admin/lgpd/requests?${params}`);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar solicitações");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, days]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (item) => {
    setDetail(item);
    setNotes(item.admin_notes || "");
  };

  const changeStatus = async (newStatus) => {
    if (!detail) return;
    setSaving(true);
    try {
      await api.patch(`/admin/lgpd/requests/${detail.id}`, {
        status: newStatus,
        admin_notes: notes,
      });
      toast.success("Solicitação atualizada.");
      setDetail(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar");
    } finally {
      setSaving(false);
    }
  };

  const counters = data.counters || {};
  const overdueCount = counters.overdue || 0;
  const openCount = (counters.received || 0) + (counters.in_progress || 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="admin-lgpd-page">
      <Link to="/admin" className="text-sm text-slate-500 hover:text-[#0B2B62] inline-flex items-center gap-1 mb-4">
        <ChevronLeft className="w-4 h-4" /> Voltar ao painel admin
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-emerald-700 font-bold">
            <ShieldCheck className="w-3.5 h-3.5" /> Governança LGPD · Art. 18
          </div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-[#0B2B62] tracking-tight mt-1">
            Solicitações do titular
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Prazo legal ANPD: 15 dias corridos (Art. 19 LGPD). Priorize itens com prazo estourado.
          </p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Atualizar
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="admin-lgpd-kpis">
        <KpiCard label="Em aberto" value={openCount} color="text-blue-700" />
        <KpiCard label="Prazo vencido" value={overdueCount} color="text-red-700" testId="admin-lgpd-kpi-overdue" />
        <KpiCard label="Em análise" value={counters.in_progress || 0} color="text-amber-700" />
        <KpiCard label="Respondidas" value={counters.responded || 0} color="text-emerald-700" />
        <KpiCard label="Rejeitadas" value={counters.rejected || 0} color="text-slate-700" />
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="admin-lgpd-filter-status"
          className="px-3 py-1.5 border border-slate-300 rounded-md text-sm bg-white">
          <option value="">Todos os status</option>
          {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}
          data-testid="admin-lgpd-filter-days"
          className="px-3 py-1.5 border border-slate-300 rounded-md text-sm bg-white">
          <option value={7}>7 dias</option>
          <option value={30}>30 dias</option>
          <option value={90}>90 dias</option>
          <option value={180}>180 dias</option>
          <option value={365}>1 ano</option>
        </select>
      </div>

      {/* Tabela */}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="min-w-[900px] w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-widest text-slate-500">Recebida em</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-widest text-slate-500">Titular</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-widest text-slate-500">Direitos</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-widest text-slate-500">Status</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-widest text-slate-500">Prazo</th>
              <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-widest text-slate-500">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="p-8 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Carregando…</td></tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-slate-500">Nenhuma solicitação neste filtro.</td></tr>
            )}
            {!loading && data.items.map((it) => {
              const st = statusMeta(it.status);
              return (
                <tr key={it.id} data-testid={`admin-lgpd-row-${it.id}`}
                  className={`border-b border-slate-100 hover:bg-slate-50 ${it.is_overdue ? "bg-red-50/40" : ""}`}>
                  <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">
                    {new Date(it.received_at).toLocaleString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-slate-800">{it.name}</div>
                    <div className="text-[11px] text-slate-500">{it.email}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {(it.type_labels || []).slice(0, 3).map((l, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-800 border border-violet-100">{l}</span>
                      ))}
                      {(it.type_labels || []).length > 3 && (
                        <span className="text-[10px] text-slate-500">+{it.type_labels.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${st.color}`}>{st.label}</span>
                  </td>
                  <td className="px-3 py-2.5"><DaysBadge days={it.days_left} status={it.status} /></td>
                  <td className="px-3 py-2.5 text-right">
                    <button onClick={() => openDetail(it)} data-testid={`admin-lgpd-open-${it.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded bg-[#0B2B62] hover:bg-[#0a2454] text-white text-xs font-bold">
                      Abrir
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal detalhe */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
          onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-4"
            onClick={(e) => e.stopPropagation()} data-testid="admin-lgpd-detail-modal">
            <div className="p-5 border-b border-slate-200 flex items-start justify-between gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Solicitação LGPD Art. 18</div>
                <h2 className="text-xl font-bold text-[#0B2B62] mt-0.5">{detail.name}</h2>
                <div className="text-xs text-slate-500 mt-1">
                  Recebida em {new Date(detail.received_at).toLocaleString("pt-BR")}
                </div>
              </div>
              <DaysBadge days={detail.days_left} status={detail.status} />
            </div>

            <div className="p-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-500" /> <a href={`mailto:${detail.email}`} className="text-[#0B2B62] underline">{detail.email}</a></div>
                {detail.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-slate-500" /> {detail.phone}</div>}
                {detail.cpf && <div className="text-slate-700">CPF: <span className="font-mono">{detail.cpf}</span></div>}
                {detail.protocol && <div className="flex items-center gap-2"><FileText className="w-3.5 h-3.5 text-slate-500" /> <span className="font-mono">{detail.protocol}</span></div>}
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Direitos solicitados</div>
                <ul className="text-sm text-slate-800 list-disc pl-5 space-y-0.5">
                  {(detail.type_labels || []).map((l, i) => <li key={i}>{l}</li>)}
                </ul>
              </div>

              {detail.message && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Mensagem do titular</div>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap break-words p-3 bg-slate-50 border-l-2 border-[#0B2B62] rounded">
                    {detail.message}
                  </div>
                </div>
              )}

              <div className="text-[11px] text-slate-500">
                IP: <span className="font-mono">{detail.ip || "—"}</span>
                {detail.user_agent && <><br />UA: <span className="font-mono break-all">{detail.user_agent}</span></>}
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                  Notas internas (visível apenas para admin)
                </label>
                <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
                  data-testid="admin-lgpd-notes"
                  placeholder="Ex.: Cliente contactada por e-mail, dados enviados via link seguro em 12/02. Aguardando confirmação."
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#0B2B62] outline-none resize-none" />
              </div>
            </div>

            <div className="p-5 border-t border-slate-200 flex flex-wrap gap-2 justify-end">
              <button onClick={() => setDetail(null)}
                className="px-3 py-2 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm">
                Fechar
              </button>
              <button onClick={() => changeStatus("in_progress")} disabled={saving}
                data-testid="admin-lgpd-set-in-progress"
                className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50">
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Marcar em análise
              </button>
              <button onClick={() => changeStatus("rejected")} disabled={saving}
                data-testid="admin-lgpd-set-rejected"
                className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 text-sm font-semibold disabled:opacity-50">
                <XCircle className="w-3.5 h-3.5" /> Rejeitar
              </button>
              <button onClick={() => changeStatus("responded")} disabled={saving}
                data-testid="admin-lgpd-set-responded"
                className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-bold disabled:opacity-50">
                <CheckCircle2 className="w-3.5 h-3.5" /> Marcar como respondida
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const KpiCard = ({ label, value, color = "text-slate-700", testId }) => (
  <div className="p-4 rounded-xl border border-slate-200 bg-white" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
    <div className={`font-display font-bold text-2xl mt-0.5 tabular-nums ${color}`}>{value}</div>
  </div>
);
